import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../../services/api';
import './WheelStrategy.css';

const BASE_ASSETS = ['شستا', 'خودرو', 'فولاد', 'اهرم', 'فملی', 'شپنا', 'خساپا'];
const TRADE_TYPES = ['فروش اختیاری', 'خرید اختیاری', 'خرید سهم پایه'];
const PHASES = ['فاز ۱', 'فاز ۲', 'خاتمه یافته'];

function getDaysLeft(jalaliDateStr) {
  if (!jalaliDateStr) return 0;
  const [jy, jm, jd] = String(jalaliDateStr).split('/').map(Number);
  if (!jy || !jm || !jd) return 0;
  const epbase = jy - (jy >= 0 ? 474 : 473);
  const epY = 474 + (epbase % 2820);
  const md = (jm - 1) * 31 - Math.floor(jm / 7) * (jm - 7);
  const targetJdn = jd + md + Math.floor((epY * 682 - 110) / 2816) + (epY - 1) * 365 + Math.floor(epbase / 2820) * 1029983 + (1948320 - 1);
  const today = new Date();
  const gy = today.getFullYear(), gm = today.getMonth() + 1, gd = today.getDate();
  const todayJdn = Math.floor((1461 * (gy + 4800 + Math.floor((gm - 14) / 12))) / 4) +
    Math.floor((367 * (gm - 2 - 12 * Math.floor((gm - 14) / 12))) / 12) -
    Math.floor((3 * Math.floor((gy + 4900 + Math.floor((gm - 14) / 12)) / 100)) / 4) + gd - 32075;
  const diff = targetJdn - todayJdn;
  return diff > 0 ? diff : 0;
}

function inferOptionType(symbol, optionType) {
  if (optionType) return optionType.toLowerCase();
  if (!symbol) return null;
  const firstChar = symbol.trim().charAt(0);
  if (firstChar === 'ض') return 'call';
  if (firstChar === 'ط') return 'put';
  return null;
}

function processWheelMetrics(rawData, overrideSymbol, overridePremium) {
  if (!rawData || rawData.length === 0) return { puts: [], calls: [] };

  const now = new Date();

  const baseShares = rawData.filter((item) => {
    const sp = item.strike_price;
    return sp === null || sp === undefined || sp === 0 || sp === '';
  });
  const underlyingMap = {};
  baseShares.forEach((item) => {
    if (item.symbol) underlyingMap[item.symbol] = Number(item.price) || 0;
  });
  const options = rawData.filter((item) => {
    const sp = item.strike_price;
    return sp !== null && sp !== undefined && sp !== 0 && sp !== '';
  });
  const puts = [];
  const calls = [];
  options.forEach((item) => {
    const symbol = item.symbol || '';
    const baseSymbol = item.base_symbol || '';
    const underlying = underlyingMap[baseSymbol] || 0;
    const strike = Number(item.strike_price) || 0;
    let premium = Number(item.price) || 0;
    if (overrideSymbol && overrideSymbol.trim() === symbol.trim() && overridePremium !== '') {
      const parsedPremium = Number(overridePremium);
      if (!isNaN(parsedPremium)) premium = parsedPremium;
    }
    const volume = Number(item.volume) || 0;
    const type = inferOptionType(symbol, item.option_type);

    // محاسبه روزهای مانده تا سررسید
    let dtm = getDaysLeft(item.maturity_date);

    // اگر قرارداد منقضی شده باشد (DTM <= 0)، آن را رد می‌کنیم
    if (dtm <= 0) return;

    const common = {
      symbol, base_symbol: baseSymbol, underlying, strike, premium, volume, dtm,
      maturity_date: item.maturity_date || '-',
    };
    if (type === 'put') {
      const marginOfSafety = underlying > 0 ? ((underlying - strike) / underlying) * 100 : 0;
      const periodYield = strike > 0 ? (premium / strike) * 100 : 0;
      const annualizedYield = periodYield * (365 / dtm);
      puts.push({ ...common, margin_of_safety: marginOfSafety, period_yield: periodYield, annualized_yield: annualizedYield });
    } else if (type === 'call') {
      const staticYield = underlying > 0 ? (premium / underlying) * 100 : 0;
      const annualizedStaticYield = staticYield * (365 / dtm);
      const ifCalledYield = underlying > 0 ? ((strike - underlying + premium) / underlying) * 100 : 0;
      const annualizedIfCalledYield = ifCalledYield * (365 / dtm);
      const downsideProtection = underlying > 0 ? (premium / underlying) * 100 : 0;
      calls.push({
        ...common,
        static_yield: staticYield,
        annualized_static_yield: annualizedStaticYield,
        if_called_yield: ifCalledYield,
        annualized_if_called_yield: annualizedIfCalledYield,
        downside_protection: downsideProtection,
      });
    }
  });
  return { puts, calls };
}

export default function WheelStrategy() {
  const [activeTab, setActiveTab] = useState('terminal');
  const [activeSubTab, setActiveSubTab] = useState('phase1');

  const [marketData, setMarketData] = useState([]);
  const [portfolioData, setPortfolioData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const [baseFilter, setBaseFilter] = useState([...BASE_ASSETS]);
  const [dtmRange, setDtmRange] = useState([1, 150]);
  const [minVolume, setMinVolume] = useState(0);
  const [enableOverride, setEnableOverride] = useState(false);
  const [overrideSymbol, setOverrideSymbol] = useState('');
  const [overridePremium, setOverridePremium] = useState('');
  const [minSafety, setMinSafety] = useState(-10);
  const [minYieldP1, setMinYieldP1] = useState(10);
  const [minYieldP2, setMinYieldP2] = useState(5);

  const [symInput, setSymInput] = useState('');
  const [tradeType, setTradeType] = useState(TRADE_TYPES[0]);
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [marginVal, setMarginVal] = useState('');
  const [hamiRate, setHamiRate] = useState('35');
  const [dtmHeld, setDtmHeld] = useState('30');
  const [phaseSelect, setPhaseSelect] = useState(PHASES[0]);
  const [tradeDate, setTradeDate] = useState(new Date().toISOString().split('T')[0]);
  const [expanderOpen, setExpanderOpen] = useState(true);

  const [p2Id, setP2Id] = useState('');
  const [strikeVal, setStrikeVal] = useState('');
  const [baseSymVal, setBaseSymVal] = useState('');
  const [delId, setDelId] = useState('');

  // State برای مرتب‌سازی
  const [sortConfig, setSortConfig] = useState({ key: 'annualized_yield', direction: 'desc' });
  const [sortConfigCalls, setSortConfigCalls] = useState({ key: 'annualized_static_yield', direction: 'desc' });

  // Ref برای حفظ موقعیت اسکرول
  const tableScrollRef = useRef(null);
  const scrollPositionRef = useRef(0);

  const fetchData = async () => {
    try {
      const [marketData, portfolioData] = await Promise.all([
        api.get('/api/market-prices-all'),
        api.get('/api/wheel-portfolio'),
      ]);
      // ابتدا داده‌ها را می‌گیریم، بعد یکباره آپدیت می‌کنیم
      setMarketData(marketData || []);
      setPortfolioData(portfolioData || []);
      setLastUpdate(new Date());
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    } catch (err) {
      console.error('Error fetching wheel data:', err);
    }
    // loading را فقط یکبار در شروع false می‌کنیم
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const { puts, calls } = useMemo(() => {
    return processWheelMetrics(marketData, enableOverride ? overrideSymbol : '', overridePremium);
  }, [marketData, enableOverride, overrideSymbol, overridePremium]);

  const filteredPuts = useMemo(() => {
    let result = puts.filter((item) => {
      const inBase = baseFilter.length === 0 || baseFilter.includes(item.base_symbol);
      const inDtm = item.dtm >= dtmRange[0] && item.dtm <= dtmRange[1];
      const inSafety = item.margin_of_safety >= minSafety;
      const inYield = item.annualized_yield >= minYieldP1;
      const inVolume = item.volume >= minVolume;
      return inBase && inDtm && inSafety && inYield && inVolume;
    });
    // مرتب‌سازی بر اساس تنظیمات کاربر (پایدار - با symbol به عنوان tiebreaker)
    result.sort((a, b) => {
      const aVal = a[sortConfig.key] || 0;
      const bVal = b[sortConfig.key] || 0;
      if (aVal !== bVal) {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      // اگر مقدارها برابر بودند، بر اساس symbol مرتب می‌کنیم برای ثبات
      return (a.symbol || '').localeCompare(b.symbol || '');
    });
    return result.map((item) => {
      let signal = 'متوسط';
      let signalClass = 'signal-medium';
      if (item.margin_of_safety >= 12) {
        if (item.volume > minVolume * 3) {
          signal = 'عالی و روان';
          signalClass = 'signal-liquid';
        } else {
          signal = 'عالی';
          signalClass = 'signal-excellent';
        }
      }
      return { ...item, Signal: signal, signalClass };
    });
  }, [puts, baseFilter, dtmRange, minSafety, minYieldP1, minVolume, sortConfig]);

  const filteredCalls = useMemo(() => {
    let result = calls.filter((item) => {
      const inBase = baseFilter.length === 0 || baseFilter.includes(item.base_symbol);
      const inDtm = item.dtm >= dtmRange[0] && item.dtm <= dtmRange[1];
      const inYield = item.annualized_static_yield >= minYieldP2;
      const inVolume = item.volume >= minVolume;
      return inBase && inDtm && inYield && inVolume;
    });
    result = result.filter((item) => item.strike >= item.underlying);
    // مرتب‌سازی بر اساس تنظیمات کاربر (پایدار - با symbol به عنوان tiebreaker)
    result.sort((a, b) => {
      const aVal = a[sortConfigCalls.key] || 0;
      const bVal = b[sortConfigCalls.key] || 0;
      if (aVal !== bVal) {
        return sortConfigCalls.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      // اگر مقدارها برابر بودند، بر اساس symbol مرتب می‌کنیم برای ثبات
      return (a.symbol || '').localeCompare(b.symbol || '');
    });
    return result.map((item) => {
      let signal = 'کسب پریمیوم ثابت';
      let signalClass = 'signal-static';
      if (item.strike > item.underlying) {
        signal = 'صعود سهم + کسب پریمیوم';
        signalClass = 'signal-bullish';
      }
      return { ...item, Signal: signal, signalClass };
    });
  }, [calls, baseFilter, dtmRange, minYieldP2, minVolume, sortConfigCalls]);

  // حفظ موقعیت اسکرول هنگام بروزرسانی داده
  useEffect(() => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollTop = scrollPositionRef.current;
    }
  }, [filteredPuts, filteredCalls]);

  const handleTableScroll = (e) => {
    scrollPositionRef.current = e.target.scrollTop;
  };

  const handleAddTrade = async () => {
    if (!symInput || !quantity || !price) {
      alert('لطفاً تمام فیلدهای الزامی را پر کنید');
      return;
    }
    try {
      await api.post('/api/wheel-portfolio', {
        symbol: symInput,
        trade_type: tradeType,
        quantity: Number(quantity),
        price: Number(price),
        trade_date: tradeDate,
        margin_required: Number(marginVal) || 0,
        hami_rate: Number(hamiRate) || 35,
        dtm_held: Number(dtmHeld) || 30,
        wheel_phase: phaseSelect,
      });
      alert('تراکنش با موفقیت ثبت شد');
      fetchData();
      setSymInput('');
      setQuantity('');
      setPrice('');
      setMarginVal('');
    } catch (err) {
      console.error(err);
      alert('خطا در ثبت تراکنش');
    }
  };

  const handlePhase2 = async () => {
    if (!p2Id || !strikeVal || !baseSymVal) {
      alert('لطفاً تمام فیلدها را پر کنید');
      return;
    }
    try {
      await api.put(`/api/wheel-portfolio/${p2Id}/phase2`, {
        strike_price: Number(strikeVal),
        base_symbol: baseSymVal,
      });
      alert('تبدیل به فاز ۲ با موفقیت انجام شد');
      fetchData();
      setP2Id('');
      setStrikeVal('');
      setBaseSymVal('');
    } catch (err) {
      console.error(err);
      alert('خطا در تبدیل به فاز ۲');
    }
  };

  const handleDelete = async () => {
    if (!delId) {
      alert('لطفاً شناسه تراکنش را وارد کنید');
      return;
    }
    try {
      await api.delete(`/api/wheel-portfolio/${delId}`);
      alert('تراکنش حذف شد');
      fetchData();
      setDelId('');
    } catch (err) {
      console.error(err);
      alert('خطا در حذف تراکنش');
    }
  };

  const enrichedPortfolio = useMemo(() => {
    return portfolioData.map((row) => {
      const margin = Number(row.margin_required) || 0;
      const rate = Number(row.hami_rate) || 0;
      const dtm = Number(row.dtm_held) || 0;
      const hamiInterest = margin > 0 ? (margin * (rate / 100) / 365 * dtm) : 0;
      return { ...row, hami_interest_earned: hamiInterest };
    });
  }, [portfolioData]);

  const kpi = useMemo(() => {
    const totalCommission = enrichedPortfolio.reduce((sum, row) => sum + (Number(row.commission) || 0), 0);
    const totalPremiumsReceived = enrichedPortfolio
      .filter((row) => row.trade_type === 'فروش اختیاری')
      .reduce((sum, row) => sum + (Number(row.net_value) || 0), 0);
    const totalPremiumsPaid = enrichedPortfolio
      .filter((row) => row.trade_type === 'خرید اختیاری')
      .reduce((sum, row) => sum + (Number(row.net_value) || 0), 0);
    const totalHamiProfit = enrichedPortfolio.reduce((sum, row) => sum + (Number(row.hami_interest_earned) || 0), 0);
    const netWheelProfit = (totalPremiumsReceived - totalPremiumsPaid) + totalHamiProfit - totalCommission;
    let totalCapitalDeployed = Math.max(...enrichedPortfolio.map((row) => Number(row.margin_required) || 0));
    if (!totalCapitalDeployed || totalCapitalDeployed === 0 || isNaN(totalCapitalDeployed)) {
      totalCapitalDeployed = enrichedPortfolio
        .filter((row) => row.trade_type === 'خرید سهم پایه')
        .reduce((sum, row) => sum + (Number(row.net_value) || 0), 0);
    }
    if (!totalCapitalDeployed || totalCapitalDeployed === 0 || isNaN(totalCapitalDeployed)) {
      totalCapitalDeployed = 1.0;
    }
    const wheelRoi = (netWheelProfit / totalCapitalDeployed) * 100;
    return {
      netWheelProfit,
      wheelRoi,
      totalHamiProfit,
      totalCommission,
      totalPremiumsReceived,
      totalPremiumsPaid,
      totalCapitalDeployed,
    };
  }, [enrichedPortfolio]);
  return (
    <div className="wheel-strategy">
      <h1>🎯 ترمینال هوشمند دوطرفه استراتژی چرخ (The Wheel Strategy)</h1>
      <p className="info-text">مدیریت همزمان فاز ۱ (فروش اختیار فروش) و فاز ۲ (فروش اختیار خرید روی سهام بنیادی)</p>

      <div className="tab-header">
        <button className={activeTab === 'terminal' ? 'active' : ''} onClick={() => setActiveTab('terminal')}>
          🎯 ترمینال استراتژی چرخ
        </button>
        <button className={activeTab === 'portfolio' ? 'active' : ''} onClick={() => setActiveTab('portfolio')}>
          💼 پورتفوی حرفه‌ای معاملاتی
        </button>
        <button className={activeTab === 'education' ? 'active' : ''} onClick={() => setActiveTab('education')}>
          📚 آموزش
        </button>
      </div>

      {activeTab === 'education' && (
        <div className="education-frame-wrapper">
          <iframe src="/education.html" title="آموزش استراتژی چرخه" className="education-iframe" />
        </div>
      )}

      {activeTab === 'terminal' && (
        <div>
          <div className="filter-panel">
            <div className="filter-group">
              <label>انتخاب سهام پایه:</label>
              <select multiple value={baseFilter} onChange={(e) => {
                const opts = Array.from(e.target.selectedOptions).map((o) => o.value);
                setBaseFilter(opts);
              }}>
                {BASE_ASSETS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div className="filter-group range-slider">
              <label>محدوده روزهای مانده تا سررسید (DTM): {dtmRange[0]} - {dtmRange[1]}</label>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <input type="range" min={1} max={150} value={dtmRange[0]} onChange={(e) => setDtmRange([Number(e.target.value), dtmRange[1]])} />
                <input type="range" min={1} max={150} value={dtmRange[1]} onChange={(e) => setDtmRange([dtmRange[0], Number(e.target.value)])} />
              </div>
              <div className="slider-labels"><span>از {dtmRange[0]}</span><span>تا {dtmRange[1]}</span></div>
            </div>
            <div className="filter-group">
              <label>حداقل حجم معاملات روزانه (قرارداد):</label>
              <input type="number" min={0} value={minVolume} onChange={(e) => setMinVolume(Number(e.target.value))} />
            </div>
            <div className="filter-group" style={{ flex: '1 1 100%' }}>
              <div className="override-panel">
                <label className="checkbox-label">
                  <input type="checkbox" checked={enableOverride} onChange={(e) => setEnableOverride(e.target.checked)} />
                  ⚡ ماشین حساب سریع مظنه خرید (Bid)
                </label>
                {enableOverride && (
                  <>
                    <input type="text" placeholder="نماد مورد نظر" value={overrideSymbol} onChange={(e) => setOverrideSymbol(e.target.value)} />
                    <input type="number" placeholder="پریمیوم پیشنهادی" value={overridePremium} onChange={(e) => setOverridePremium(e.target.value)} />
                  </>
                )}
                {enableOverride && overrideSymbol && overridePremium && Number(overridePremium) > 0 && (
                  <div className="text-warning" style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                    پریمیوم نماد {overrideSymbol} موقتاً به {Number(overridePremium).toLocaleString()} ریال تغییر یافت.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="sub-tab-header">
            <button className={activeSubTab === 'phase1' ? 'active' : ''} onClick={() => setActiveSubTab('phase1')}>
              🟢 فاز اول: تعهد خرید (Cash-Secured Put)
            </button>
            <button className={activeSubTab === 'phase2' ? 'active' : ''} onClick={() => setActiveSubTab('phase2')}>
              🔵 فاز دوم: تعهد فروش (Covered Call)
            </button>
          </div>

          {activeSubTab === 'phase1' && (
            <div>
              <h3 className="section-title">📥 کسب درآمد نقد جهت خرید سهم زیر قیمت بازار</h3>
              <div className="filter-group" style={{ marginBottom: 16 }}>
                <label>حداقل حاشیه امنیت فاز اول %: {minSafety}</label>
                <input type="range" min={-10} max={40} value={minSafety} onChange={(e) => setMinSafety(Number(e.target.value))} />
              </div>
              <div className="filter-group" style={{ marginBottom: 16 }}>
                <label>حداقل بازدهی سالانه‌شده فاز اول %: {minYieldP1}</label>
                <input type="range" min={10} max={150} value={minYieldP1} onChange={(e) => setMinYieldP1(Number(e.target.value))} />
              </div>
              {isInitialLoad ? (
                <div className="loading-state">در حال بارگذاری...</div>
              ) : filteredPuts.length === 0 ? (
                <div className="empty-state">داده‌ای با فیلترها و حجم معاملات مشخص شده همخوانی ندارد.</div>
              ) : (
                <div className="table-container" ref={tableScrollRef} onScroll={handleTableScroll}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th onClick={() => setSortConfig({ key: 'symbol', direction: sortConfig.key === 'symbol' && sortConfig.direction === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer' }}>نماد (ط) {sortConfig.key === 'symbol' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                        <th onClick={() => setSortConfig({ key: 'base_symbol', direction: sortConfig.key === 'base_symbol' && sortConfig.direction === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer' }}>سهم پایه {sortConfig.key === 'base_symbol' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                        <th onClick={() => setSortConfig({ key: 'underlying', direction: sortConfig.key === 'underlying' && sortConfig.direction === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer' }}>قیمت سهم {sortConfig.key === 'underlying' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                        <th onClick={() => setSortConfig({ key: 'strike', direction: sortConfig.key === 'strike' && sortConfig.direction === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer' }}>قیمت اعمال {sortConfig.key === 'strike' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                        <th onClick={() => setSortConfig({ key: 'premium', direction: sortConfig.key === 'premium' && sortConfig.direction === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer' }}>پریمیوم دریافتی {sortConfig.key === 'premium' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                        <th onClick={() => setSortConfig({ key: 'dtm', direction: sortConfig.key === 'dtm' && sortConfig.direction === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer' }}>روز تا سررسید {sortConfig.key === 'dtm' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                        <th onClick={() => setSortConfig({ key: 'volume', direction: sortConfig.key === 'volume' && sortConfig.direction === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer' }}>حجم روزانه {sortConfig.key === 'volume' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                        <th onClick={() => setSortConfig({ key: 'margin_of_safety', direction: sortConfig.key === 'margin_of_safety' && sortConfig.direction === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer' }}>حاشیه امنیت (%) {sortConfig.key === 'margin_of_safety' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                        <th onClick={() => setSortConfig({ key: 'annualized_yield', direction: sortConfig.key === 'annualized_yield' && sortConfig.direction === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer' }}>بازدهی سالانه‌شده (%) {sortConfig.key === 'annualized_yield' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                        <th>وضعیت نقدشوندگی و ریسک</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPuts.map((row) => (
                        <tr key={row.symbol} className={enableOverride && row.symbol === overrideSymbol ? 'override-highlight' : ''}>
                          <td className="symbol-cell">{row.symbol}</td>
                          <td>{row.base_symbol}</td>
                          <td>{row.underlying.toLocaleString()}</td>
                          <td>{row.strike.toLocaleString()}</td>
                          <td>{row.premium.toLocaleString()}</td>
                          <td>{row.dtm}</td>
                          <td>{row.volume.toLocaleString()}</td>
                          <td className={row.margin_of_safety >= 0 ? 'text-positive' : 'text-negative'}>{row.margin_of_safety.toFixed(1)}%</td>
                          <td className="text-primary">{row.annualized_yield.toFixed(1)}%</td>
                          <td><span className={`signal-tag ${row.signalClass}`}>{row.Signal}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeSubTab === 'phase2' && (
            <div>
              <h3 className="section-title">📤 تعهد فروش سهم‌های موجود در پرتفوی با سود اضافه (امن)</h3>
              <p className="info-text">💡 در این بخش طبق استراتژی چرخ، تنها قراردادهایی نمایش داده می‌شوند که قیمت اعمال آن‌ها از قیمت فعلی سهم بالاتر (یا مساوی) است تا دارایی شما ارزان‌تر از ارزش روز به فروش نرسد.</p>
              <div className="filter-group" style={{ marginBottom: 16 }}>
                <label>حداقل بازدهی ثابت سالانه‌شده فاز دوم %: {minYieldP2}</label>
                <input type="range" min={5} max={100} value={minYieldP2} onChange={(e) => setMinYieldP2(Number(e.target.value))} />
              </div>
              {isInitialLoad ? (
                <div className="loading-state">در حال بارگذاری...</div>
              ) : filteredCalls.length === 0 ? (
                <div className="empty-state">داده‌ای با فیلترها و حجم معاملات مشخص شده همخوانی ندارد.</div>
              ) : (
                <div className="table-container" ref={tableScrollRef} onScroll={handleTableScroll}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th onClick={() => setSortConfigCalls({ key: 'symbol', direction: sortConfigCalls.key === 'symbol' && sortConfigCalls.direction === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer' }}>نماد (ض) {sortConfigCalls.key === 'symbol' && (sortConfigCalls.direction === 'asc' ? '▲' : '▼')}</th>
                        <th onClick={() => setSortConfigCalls({ key: 'base_symbol', direction: sortConfigCalls.key === 'base_symbol' && sortConfigCalls.direction === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer' }}>سهم پایه {sortConfigCalls.key === 'base_symbol' && (sortConfigCalls.direction === 'asc' ? '▲' : '▼')}</th>
                        <th onClick={() => setSortConfigCalls({ key: 'underlying', direction: sortConfigCalls.key === 'underlying' && sortConfigCalls.direction === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer' }}>قیمت فعلی سهم {sortConfigCalls.key === 'underlying' && (sortConfigCalls.direction === 'asc' ? '▲' : '▼')}</th>
                        <th onClick={() => setSortConfigCalls({ key: 'strike', direction: sortConfigCalls.key === 'strike' && sortConfigCalls.direction === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer' }}>قیمت اعمال تعهد {sortConfigCalls.key === 'strike' && (sortConfigCalls.direction === 'asc' ? '▲' : '▼')}</th>
                        <th onClick={() => setSortConfigCalls({ key: 'premium', direction: sortConfigCalls.key === 'premium' && sortConfigCalls.direction === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer' }}>پریمیوم دریافتی {sortConfigCalls.key === 'premium' && (sortConfigCalls.direction === 'asc' ? '▲' : '▼')}</th>
                        <th onClick={() => setSortConfigCalls({ key: 'dtm', direction: sortConfigCalls.key === 'dtm' && sortConfigCalls.direction === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer' }}>روز تا سررسید {sortConfigCalls.key === 'dtm' && (sortConfigCalls.direction === 'asc' ? '▲' : '▼')}</th>
                        <th onClick={() => setSortConfigCalls({ key: 'volume', direction: sortConfigCalls.key === 'volume' && sortConfigCalls.direction === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer' }}>حجم روزانه {sortConfigCalls.key === 'volume' && (sortConfigCalls.direction === 'asc' ? '▲' : '▼')}</th>
                        <th onClick={() => setSortConfigCalls({ key: 'static_yield', direction: sortConfigCalls.key === 'static_yield' && sortConfigCalls.direction === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer' }}>بازدهی نقد (%) {sortConfigCalls.key === 'static_yield' && (sortConfigCalls.direction === 'asc' ? '▲' : '▼')}</th>
                        <th onClick={() => setSortConfigCalls({ key: 'annualized_static_yield', direction: sortConfigCalls.key === 'annualized_static_yield' && sortConfigCalls.direction === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer' }}>بازدهی نقد سالانه‌شده (%) {sortConfigCalls.key === 'annualized_static_yield' && (sortConfigCalls.direction === 'asc' ? '▲' : '▼')}</th>
                        <th onClick={() => setSortConfigCalls({ key: 'if_called_yield', direction: sortConfigCalls.key === 'if_called_yield' && sortConfigCalls.direction === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer' }}>بازدهی کل با صعود (%) {sortConfigCalls.key === 'if_called_yield' && (sortConfigCalls.direction === 'asc' ? '▲' : '▼')}</th>
                        <th>کاور ریزش (%)</th>
                        <th>سناریو</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCalls.map((row) => (
                        <tr key={row.symbol} className={enableOverride && row.symbol === overrideSymbol ? 'override-highlight' : ''}>
                          <td className="symbol-cell">{row.symbol}</td>
                          <td>{row.base_symbol}</td>
                          <td>{row.underlying.toLocaleString()}</td>
                          <td>{row.strike.toLocaleString()}</td>
                          <td>{row.premium.toLocaleString()}</td>
                          <td>{row.dtm}</td>
                          <td>{row.volume.toLocaleString()}</td>
                          <td className="text-primary">{row.static_yield.toFixed(1)}%</td>
                          <td className="text-positive">{row.annualized_static_yield.toFixed(1)}%</td>
                          <td className="text-primary">{row.if_called_yield.toFixed(1)}%</td>
                          <td className="text-warning">{row.downside_protection.toFixed(1)}%</td>
                          <td><span className={`signal-tag ${row.signalClass}`}>{row.Signal}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'portfolio' && (
        <div>
          <h2>💼 پورتفوی هوشمند با قابلیت سود سپرده حامی و تبدیل به سهم</h2>
          <p className="info-text">پایش سود خالص دریافتی، اثر سپرده حامی بر وجه تضمین بلوکه شده و مدیریت گام‌به‌گام فازهای چرخ.</p>

          <div className={`expander-panel ${expanderOpen ? 'open' : ''}`}>
            <div className="expander-header" onClick={() => setExpanderOpen(!expanderOpen)}>
              <h3>📥 ثبت تراکنش جدید در پورتفوی استراتژی چرخ</h3>
              <span className="expander-icon">{expanderOpen ? '▲' : '▼'}</span>
            </div>
            {expanderOpen && (
              <div className="expander-body">
                <div className="form-grid">
                  <div className="form-group"><label>نماد معامله شده (اختیار یا سهم):</label><input type="text" value={symInput} onChange={(e) => setSymInput(e.target.value)} placeholder="مثلاً طخودرو4058" /></div>
                  <div className="form-group"><label>نوع موقعیت:</label><select value={tradeType} onChange={(e) => setTradeType(e.target.value)}>{TRADE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
                  <div className="form-group"><label>تعداد معامله (قرارداد/برگه سهم):</label><input type="number" min={1} value={quantity} onChange={(e) => setQuantity(e.target.value)} /></div>
                  <div className="form-group"><label>قیمت معامله شده (ریال):</label><input type="number" min={1} value={price} onChange={(e) => setPrice(e.target.value)} /></div>
                  <div className="form-group"><label>وجه تضمین بلوکه شده کل (ریال):</label><input type="number" min={0} value={marginVal} onChange={(e) => setMarginVal(e.target.value)} /></div>
                  <div className="form-group"><label>نرخ سود حامی سالانه کارگزاری (%):</label><input type="number" min={0} max={100} value={hamiRate} onChange={(e) => setHamiRate(e.target.value)} /></div>
                  <div className="form-group"><label>تعداد روزهای نگهداشت موقعیت:</label><input type="number" min={1} max={365} value={dtmHeld} onChange={(e) => setDtmHeld(e.target.value)} /></div>
                  <div className="form-group"><label>فاز استراتژی:</label><select value={phaseSelect} onChange={(e) => setPhaseSelect(e.target.value)}>{PHASES.map((p) => <option key={p} value={p}>{p}</option>)}</select></div>
                  <div className="form-group"><label>تاریخ معامله:</label><input type="date" value={tradeDate} onChange={(e) => setTradeDate(e.target.value)} /></div>
                </div>
                <button className="btn-primary" onClick={handleAddTrade} style={{ width: '100%', marginTop: 8 }}>💾 ثبت تراکنش بورس و محاسبه تراز مالی</button>
              </div>
            )}
          </div>

          {enrichedPortfolio.length > 0 && (
            <>
              <h3 className="section-title">📊 وضعیت کل سود و تراز نهایی چرخ</h3>
              <div className="metric-cards">
                <div className={`metric-card ${kpi.netWheelProfit >= 0 ? 'positive' : 'negative'}`}>
                  <div className="metric-label">سود یا زیان خالص نهایی چرخ</div>
                  <div className="metric-value">{Math.round(kpi.netWheelProfit).toLocaleString()} ریال</div>
                </div>
                <div className="metric-card primary">
                  <div className="metric-label">نرخ بازدهی کل دوره چرخ (ROI)</div>
                  <div className="metric-value">{kpi.wheelRoi.toFixed(2)}%</div>
                </div>
                <div className="metric-card warning">
                  <div className="metric-label">کل سود حامی دریافت شده (وجه تضمین)</div>
                  <div className="metric-value">{Math.round(kpi.totalHamiProfit).toLocaleString()} ریال</div>
                </div>
                <div className="metric-card negative">
                  <div className="metric-label">کل کارمزدهای کسر شده بورس</div>
                  <div className="metric-value">{Math.round(kpi.totalCommission).toLocaleString()} ریال</div>
                </div>
              </div>

              <h3 className="section-title">📋 جزییات موقعیت‌های باز و بسته شده</h3>
              <div className="table-container" ref={tableScrollRef} onScroll={handleTableScroll}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>شناسه</th><th>نماد</th><th>نوع معامله</th><th>تعداد</th>
                      <th>قیمت/پریمیوم (ریال)</th><th>تاریخ</th><th>کارمزد (ریال)</th>
                      <th>ارزش خالص (ریال)</th><th>وجه تضمین (ریال)</th><th>نرخ حامی (%)</th>
                      <th>روزهای نگهداشت</th><th>فاز چرخ</th><th>سود حامی کسب‌شده</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrichedPortfolio.map((row) => (
                      <tr key={row.id}>
                        <td className="symbol-cell">{row.id}</td>
                        <td>{row.symbol}</td>
                        <td>{row.trade_type}</td>
                        <td>{Number(row.quantity).toLocaleString()}</td>
                        <td>{Number(row.price).toLocaleString()}</td>
                        <td>{row.trade_date}</td>
                        <td>{Number(row.commission).toLocaleString()}</td>
                        <td>{Number(row.net_value).toLocaleString()}</td>
                        <td>{Number(row.margin_required).toLocaleString()}</td>
                        <td>{Number(row.hami_rate).toFixed(1)}%</td>
                        <td>{row.dtm_held}</td>
                        <td>{row.wheel_phase}</td>
                        <td className="text-warning">{Math.round(row.hami_interest_earned).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h3 className="section-title">⚡ عملیات و مدیریت پورتفوی</h3>
              <div className="operations-grid">
                <div className="operation-card">
                  <h3>🔄 تبدیل تعهد خرید به سهم (ورود به فاز دوم)</h3>
                  <p className="info-text">اگر در سررسید اوراق تعهد خرید (Put) اعمال شده و ملزم به خرید سهم شدید، شناسه معامله را وارد کرده و دکمه زیر را فشار دهید.</p>
                  <div className="form-grid">
                    <div className="form-group"><label>شناسه تراکنش پوت جهت تحویل سهم:</label><input type="number" min={1} value={p2Id} onChange={(e) => setP2Id(e.target.value)} /></div>
                    <div className="form-group"><label>قیمت اعمال توافق شده (ریال):</label><input type="number" min={100} value={strikeVal} onChange={(e) => setStrikeVal(e.target.value)} /></div>
                    <div className="form-group"><label>نماد سهم پایه تحویل گرفته شده:</label><input type="text" value={baseSymVal} onChange={(e) => setBaseSymVal(e.target.value)} placeholder="مثلاً خودرو" /></div>
                  </div>
                  <button className="btn-success" onClick={handlePhase2} style={{ width: '100%', marginTop: 12 }}>🚀 اجرای فیزیکی اعمال و تولید فاز ۲ سهم پایه</button>
                </div>
                <div className="operation-card">
                  <h3>🗑️ حذف تراکنش‌های اشتباه</h3>
                  <p className="info-text">در صورت ثبت اشتباهی تراکنش، شناسه آن را وارد کرده و دکمه حذف را بزنید.</p>
                  <div className="form-group">
                    <label>شناسه تراکنش برای حذف:</label>
                    <input type="number" min={1} value={delId} onChange={(e) => setDelId(e.target.value)} />
                  </div>
                  <button className="btn-danger" onClick={handleDelete} style={{ width: '100%', marginTop: 12 }}>❌ حذف تراکنش انتخاب شده</button>
                </div>
              </div>
            </>
          )}

          {enrichedPortfolio.length === 0 && (
            <div className="empty-state">پورتفوی شما خالی است. لطفاً اولین معامله خود را از فرم بالای صفحه ثبت کنید!</div>
          )}
        </div>
      )}

      <div className="last-update">
        ⏱️ زمان آخرین بروزرسانی: {lastUpdate ? lastUpdate.toLocaleTimeString('fa-IR') : '-'}
      </div>
    </div>
  );
}

